import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useStudent } from "@/context/StudentContext";
import { PageHeader } from "@/components/PageHeader";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const badge = (st) =>
  st === "Lunas" ? "bg-green-500 text-white" : st === "Sebagian" ? "bg-yellow-400 text-yellow-900" : "bg-red-500 text-white";
const label = (st) => (st === "Belum" ? "Belum" : st);
const rupiah = (n) => `Rp ${(Number(n) || 0).toLocaleString("id-ID")},-`;
const fmtPaid = (iso) => { if (!iso) return "-"; try { return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }); } catch { return "-"; } };

export default function SPP() {
  const [params] = useSearchParams();
  const { activeId, active } = useStudent();
  const sid = params.get("student") || activeId;
  const [list, setList] = useState([]);
  const year = new Date().getFullYear();
  const isSD = active?.kelas?.startsWith("Kelas ");
  const defAmt = isSD ? 500000 : 400000;

  useEffect(() => {
    const url = sid ? `/spp?student_id=${sid}` : "/spp";
    api.get(url).then((r) => setList(r.data));
  }, [sid]);

  return (
    <div>
      <PageHeader title="Pembayaran SPP" />
      <div className="px-5 pt-4 pb-10">
        {/* Student header beside TK logo */}
        <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4 mb-4 flex items-center gap-4" data-testid="spp-student-header">
          <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center shrink-0 overflow-hidden">
            <img src={isSD ? "/sdqu_logo.png" : "/tk_logo.png"} alt="Muslim Kids" className="w-full h-full object-contain" />
          </div>
          <div className="min-w-0">
            <p className="font-heading font-bold text-stone-800 text-base leading-tight truncate">{active?.name || "—"}</p>
            <p className="text-stone-500 text-sm">Kelas {active?.kelas || "—"}</p>
            <p className="text-stone-400 text-xs">NIS: {active?.nis || "—"}</p>
          </div>
        </div>

        {/* Colorful SPP table (horizontal scroll on small screens) */}
        <div className="relative rounded-2xl overflow-hidden border border-green-200 shadow-sm" data-testid="spp-table">
          <img src={isSD ? "/sdqu_logo.png" : "/tk_logo.png"} alt="" aria-hidden data-testid="spp-watermark"
            className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 object-contain z-10" style={{ opacity: 0.15 }} />
          <table className="relative w-full border-collapse text-sm min-w-[400px]">
            <thead>
              <tr className="bg-green-800 text-white text-left">
                <th className="px-3 py-2.5 font-bold whitespace-nowrap">Bulan</th>
                <th className="px-2 py-2.5 font-bold text-center whitespace-nowrap">SPP</th>
                <th className="px-2 py-2.5 font-bold text-center whitespace-nowrap">Tgl Bayar</th>
                <th className="px-3 py-2.5 font-bold">Ket</th>
              </tr>
            </thead>
            <tbody>
              {MONTHS.map((m, i) => {
                const bill = list.find((b) => b.month === m && String(b.year) === String(year));
                const st = bill?.status || "Belum";
                return (
                  <tr key={m} data-testid={`spp-row-${m}`} className={i % 2 === 0 ? "bg-green-50" : "bg-white"}>
                    <td className="px-3 py-2.5 font-semibold text-stone-700 border-t border-green-100 whitespace-nowrap">{MONTHS_SHORT[i]} {String(year).slice(-2)}</td>
                    <td className="px-2 py-2.5 text-center border-t border-green-100 whitespace-nowrap">
                      <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-bold ${badge(st)}`} data-testid={`spp-amount-${m}`}>{rupiah(bill?.amount ?? defAmt)}</span>
                    </td>
                    <td className="px-2 py-2.5 text-center text-stone-500 text-xs border-t border-green-100 whitespace-nowrap">{fmtPaid(bill?.paid_date)}</td>
                    <td className="px-3 py-2.5 text-stone-500 text-xs border-t border-green-100">{bill?.note || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Doa & Hadits */}
        <div className="mt-5 bg-white rounded-2xl border border-stone-200/60 shadow-sm p-5 text-center" data-testid="spp-doa">
          <p className="italic text-stone-600 text-sm leading-relaxed text-center">
            Semoga setiap rupiah yang Ayah/Bunda keluarkan untuk<br />
            pendidikan ananda menjadi amal jariyah yang bernilai pahala di sisi Allah Subhanahu wata'ala serta<br />
            mendatangkan keberkahan bagi keluarga.
          </p>
          <p className="text-stone-600 text-sm text-center mb-4 mt-5">Rasulullah shallallahu 'alaihi wa sallam bersabda,</p>
          <p dir="rtl" lang="ar" className="text-center text-2xl md:text-3xl leading-loose text-stone-800 font-semibold mb-4" style={{ fontFamily: "'Traditional Arabic','Scheherazade New','Amiri',serif" }}>
            إِذَا مَاتَ الْإِنْسَانُ انْقَطَعَ عَمَلُهُ إِلَّا مِنْ ثَلَاثَةٍ مِنْ صَدَقَةٍ جَارِيَةٍ وَعِلْمٍ يُنْتَفَعُ بِهِ وَوَلَدٍ صَالِحٍ يَدْعُو لَهُ
          </p>
          <p className="italic text-stone-600 text-sm leading-relaxed text-center">
            "Jika seseorang meninggal dunia, maka terputuslah amalannya kecuali tiga perkara (yaitu): sedekah jariyah, ilmu yang diambil manfaatnya, atau doa anak yang shalih."<br />
            (HR. Muslim no. 1631)
          </p>
        </div>
      </div>
    </div>
  );
}
